import { Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { DisplayTable } from '@/components/tables/DisplayTable/DisplayTable';
import { useReportError } from '@/hooks/useReportError';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { useFamilyTreeContext } from '@/modules/familyTree/contexts';
import { useUpdateFamilyTreeRelationshipMutation } from '@/modules/familyTree/graphql/UpdateFamilyTreeRelationship.generated';
import {
  filterLinkableOptions,
  type RelationshipVariant,
} from '@/modules/familyTree/utils/linkableOptionsFilter';
import { FamilyRelationships } from '@/modules/familyTree/utils/relationshipUtils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { ClientProfileRelationshipType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { PersonRow } from './PersonRow';

export interface FamilyTreeRelationshipSectionProps {
  variant: RelationshipVariant;
  relationships: FamilyRelationships;
  onSelectPerson?: (personId: string) => void;
}

export function FamilyTreeRelationshipSection({
  variant,
  relationships,
  onSelectPerson,
}: FamilyTreeRelationshipSectionProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { state, onAfterEdit } = useFamilyTreeContext();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [isClientProfileModalOpen, setIsClientProfileModalOpen] =
    useState(false);

  // Check if the selected person is a primary client
  const isSelectedPersonPrimaryClient = useMemo(() => {
    if (!state.selectedPersonId || !state.familyTreeData?.people) {
      return false;
    }

    const selectedPerson = state.familyTreeData.people.find(
      (person) => person.clientProfile.id === state.selectedPersonId
    );

    return selectedPerson?.clientProfile.isPrimary ?? false;
  }, [state.selectedPersonId, state.familyTreeData?.people]);

  // Get display text for the modal heading based on variant
  const clientProfileTypeDisplay = (() => {
    switch (variant) {
      case 'parents':
        return 'parent';
      case 'spouse':
        return 'spouse/partner';
      case 'children':
        return 'child';
    }
  })();

  // Generate initial relationships for new individual based on variant
  const initialRelationships = useMemo(() => {
    if (!state.selectedPersonId) return undefined;

    // Determine relationship type based on variant
    const relationshipType = (() => {
      switch (variant) {
        case 'parents':
          return ClientProfileRelationshipType.Parent;
        case 'spouse':
          return ClientProfileRelationshipType.Spouse;
        case 'children':
          return ClientProfileRelationshipType.Child;
      }
    })();

    // For creating relationships, the semantics are:
    // - The NEW person is related TO the selected person
    // - toClientProfileID = selected person's ID
    // - type = the relationship type from the NEW person's perspective
    return {
      [state.selectedPersonId]: relationshipType,
    };
  }, [state.selectedPersonId, variant]);

  const getRelationshipTypeForVariant = (): ClientProfileRelationshipType => {
    switch (variant) {
      case 'parents':
        return ClientProfileRelationshipType.Parent;
      case 'spouse':
        return ClientProfileRelationshipType.Spouse;
      case 'children':
        return ClientProfileRelationshipType.Child;
    }
  };

  const [updateRelationship, { loading: isUpdatingRelationship }] =
    useUpdateFamilyTreeRelationshipMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onCompleted: () => {
        showFeedback('Relationship updated successfully', {
          variant: 'success',
        });
        onAfterEdit?.();
      },
      onError: (error) => {
        showFeedback('Failed to update relationship', { variant: 'error' });
        reportError('Failed to update relationship', error);
      },
    });

  const people = (() => {
    switch (variant) {
      case 'parents':
        return [
          ...relationships[ClientProfileRelationshipType.Parent],
          ...relationships[ClientProfileRelationshipType.StepParent],
        ];
      case 'spouse': {
        return [
          ...relationships[ClientProfileRelationshipType.Spouse],
          ...relationships[ClientProfileRelationshipType.DomesticPartner],
          ...relationships[ClientProfileRelationshipType.ExSpouse],
          ...relationships[ClientProfileRelationshipType.ExDomesticPartner],
        ];
      }
      case 'children':
        return [
          ...relationships[ClientProfileRelationshipType.Child],
          ...relationships[ClientProfileRelationshipType.StepChild],
        ];
    }
  })();

  const getRelationshipType = (
    personId: string
  ):
    | 'Spouse'
    | 'Partner'
    | 'Ex-spouse'
    | 'Ex-partner'
    | 'Parent'
    | 'Stepparent'
    | 'Child'
    | 'Stepchild'
    | '' => {
    if (variant === 'spouse') {
      if (
        relationships[ClientProfileRelationshipType.Spouse].some(
          (p) => p.id === personId
        )
      ) {
        return 'Spouse';
      }
      if (
        relationships[ClientProfileRelationshipType.DomesticPartner].some(
          (p) => p.id === personId
        )
      ) {
        return 'Partner';
      }
      if (
        relationships[ClientProfileRelationshipType.ExSpouse].some(
          (p) => p.id === personId
        )
      ) {
        return 'Ex-spouse';
      }
      if (
        relationships[ClientProfileRelationshipType.ExDomesticPartner].some(
          (p) => p.id === personId
        )
      ) {
        return 'Ex-partner';
      }
    }
    if (variant === 'children') {
      if (
        relationships[ClientProfileRelationshipType.Child].some(
          (p) => p.id === personId
        )
      ) {
        return 'Child';
      }
      if (
        relationships[ClientProfileRelationshipType.StepChild].some(
          (p) => p.id === personId
        )
      ) {
        return 'Stepchild';
      }
    }
    if (variant === 'parents') {
      if (
        relationships[ClientProfileRelationshipType.Parent].some(
          (p) => p.id === personId
        )
      ) {
        return 'Parent';
      }
      if (
        relationships[ClientProfileRelationshipType.StepParent].some(
          (p) => p.id === personId
        )
      ) {
        return 'Stepparent';
      }
    }

    return '';
  };

  const handleAddRelationship = async (toClientProfileId: string) => {
    if (!householdId || !state.selectedPersonId) return;

    // Determine the correct from/to based on relationship type
    // For Parent: the new person is the parent OF the selected person
    // For Child: the new person is the child OF the selected person
    // For Spouse: the selected person is the spouse OF the new person
    let relationshipType = getRelationshipTypeForVariant();

    // If adding a spouse, check if either person already has a spouse
    if (variant === 'spouse') {
      const selectedPersonHasSpouse =
        relationships[ClientProfileRelationshipType.Spouse].length > 0;

      // Check if the target person already has a spouse
      const allClientProfiles = getNodes(state.clientProfiles);
      const targetPerson = allClientProfiles.find(
        (p) => p.id === toClientProfileId
      );
      const targetPersonHasSpouse = targetPerson?.relationships?.some(
        (rel) =>
          rel?.type === ClientProfileRelationshipType.Spouse &&
          (rel.fromClientProfile.id === toClientProfileId ||
            rel.toClientProfile.id === toClientProfileId)
      );

      // If either person already has a spouse, create as ex-spouse
      if (selectedPersonHasSpouse || targetPersonHasSpouse) {
        relationshipType = ClientProfileRelationshipType.ExSpouse;
      }
    }

    const isParentVariant = variant === 'parents';
    const isChildVariant = variant === 'children';

    const fromClientProfileID =
      isParentVariant || isChildVariant
        ? toClientProfileId
        : state.selectedPersonId;
    const toClientProfileID =
      isParentVariant || isChildVariant
        ? state.selectedPersonId
        : toClientProfileId;

    try {
      await updateRelationship({
        variables: {
          householdId,
          clientRelationships: [
            {
              fromClientProfileID,
              toClientProfileID,
              id: null,
              relationship: {
                type: relationshipType,
              },
            },
          ],
        },
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const [unlinkingPersonId, setUnlinkingPersonId] = useState<string | null>(
    null
  );

  const handleUnlinkRelationship = async (
    personId: string,
    relationshipId: string
  ) => {
    if (!householdId || !state.selectedPersonId) return;

    setUnlinkingPersonId(personId);

    // Find the relationship to get the from/to IDs
    const allRelationships = state.clientProfiles?.edges?.flatMap(
      (edge) => edge?.node?.relationships ?? []
    );
    const relationship = allRelationships?.find(
      (r) => r?.id === relationshipId
    );

    if (!relationship) return;

    try {
      await updateRelationship({
        variables: {
          householdId,
          clientRelationships: [
            {
              fromClientProfileID: relationship.fromClientProfile.id,
              toClientProfileID: relationship.toClientProfile.id,
              id: relationshipId,
              clearRelationship: true,
            },
          ],
        },
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    } finally {
      setUnlinkingPersonId(null);
    }
  };

  const findRelationship = (personId: string) => {
    if (!state.selectedPersonId) return null;

    // Get all relationships from client profiles
    const allRelationships = state.clientProfiles?.edges?.flatMap(
      (edge) => edge?.node?.relationships ?? []
    );

    // Find the relationship between the selected person and this person
    const relationship = allRelationships?.find((r) => {
      if (!r) return false;

      // Check if this relationship connects the two people in either direction
      const connectsForward =
        r.fromClientProfile.id === state.selectedPersonId &&
        r.toClientProfile.id === personId;
      const connectsBackward =
        r.fromClientProfile.id === personId &&
        r.toClientProfile.id === state.selectedPersonId;

      return connectsForward || connectsBackward;
    });

    return relationship;
  };

  const handleChangeRelationshipType = async (
    relationshipId: string,
    fromClientProfileId: string,
    toClientProfileId: string,
    newType: ClientProfileRelationshipType
  ) => {
    if (!householdId || !state.selectedPersonId) return;

    // Determine the correct from/to based on the relationship type and variant
    // For parent/stepparent types: from = parent, to = child
    // For child/stepchild types: from = child, to = parent
    let correctFromId = fromClientProfileId;
    let correctToId = toClientProfileId;

    const isParentType =
      newType === ClientProfileRelationshipType.Parent ||
      newType === ClientProfileRelationshipType.StepParent;
    const isChildType =
      newType === ClientProfileRelationshipType.Child ||
      newType === ClientProfileRelationshipType.StepChild;

    if (isParentType) {
      // For parent types: from should be the OTHER person (parent), to should be selected person (child)
      if (fromClientProfileId === state.selectedPersonId) {
        // Need to swap: selected person should be the "to" (child)
        correctFromId = toClientProfileId;
        correctToId = fromClientProfileId;
      }
    } else if (isChildType) {
      // For child types: from should be the OTHER person (child), to should be selected person (parent)
      if (fromClientProfileId === state.selectedPersonId) {
        // Need to swap: selected person should be the "to" (parent)
        correctFromId = toClientProfileId;
        correctToId = fromClientProfileId;
      }
    }

    try {
      await updateRelationship({
        variables: {
          householdId,
          clientRelationships: [
            {
              fromClientProfileID: correctFromId,
              toClientProfileID: correctToId,
              id: relationshipId,
              relationship: {
                type: newType,
              },
            },
          ],
        },
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const linkableOptions = useMemo(() => {
    // Get all client profiles in the household (including unlinked ones)
    const allClientProfiles = getNodes(state.clientProfiles);

    // Filter and mark disabled options based on existing relationships and generation
    return filterLinkableOptions(
      allClientProfiles,
      state.selectedPersonId ?? null,
      variant,
      relationships,
      state.familyTreeData?.people ?? []
    );
  }, [
    state.clientProfiles,
    state.familyTreeData?.people,
    state.selectedPersonId,
    variant,
    relationships,
  ]);

  return (
    <Stack spacing={1} data-testid={`relationship-section-${variant}`}>
      {isClientProfileModalOpen && householdId && (
        <ClientProfileSideDrawer
          isOpen={isClientProfileModalOpen}
          setIsOpen={setIsClientProfileModalOpen}
          householdId={householdId}
          clientProfileId={null}
          clientProfileTypeDisplay={clientProfileTypeDisplay}
          hideRelationshipInput={!isSelectedPersonPrimaryClient}
          initialRelationships={initialRelationships}
          // In presentation/design mode, refetch + re-measure after creating an
          // individual (same as other in-tree edits). No-op elsewhere.
          onAfterSave={onAfterEdit}
        />
      )}
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          {variant === 'parents' && (
            <Typography variant="title-m">Parents</Typography>
          )}
          {variant === 'spouse' && (
            <Typography variant="title-m">Spouse or Ex-spouse</Typography>
          )}
          {variant === 'children' && (
            <Typography variant="title-m">Children</Typography>
          )}
          <ButtonWithPopover
            label="Add"
            startIcon={PlusIcon}
            buttonSx={{
              whiteSpace: 'nowrap',
            }}
            size="xs"
            variant="secondary"
            popperVariant="menuBelow"
            loading={isUpdatingRelationship && unlinkingPersonId === null}
          >
            <MenuItem
              icon={<PlusIcon color={COLORS.TEXT.SECONDARY} size={16} />}
              onClick={() => {
                setIsClientProfileModalOpen(true);
              }}
              label="Add new individual"
              muiMenuItemProps={{
                divider: (linkableOptions?.length ?? 0) > 0 ? true : false,
              }}
            />
            {linkableOptions?.flatMap(({ id, displayName, disabled }) => {
              return (
                <MenuItem
                  key={id}
                  label={displayName}
                  onClick={() => {
                    if (!disabled && !isUpdatingRelationship) {
                      handleAddRelationship(id);
                    }
                  }}
                  muiMenuItemProps={{
                    disabled: disabled || isUpdatingRelationship,
                  }}
                />
              );
            })}
          </ButtonWithPopover>
        </Stack>
        {people.length === 0 && (
          <Typography variant="body-xs">None specified</Typography>
        )}
      </Stack>
      <DisplayTable
        hideTableHead
        showOuterRowBorders
        columns={[{ headerName: 'Individual' }, { headerName: 'Actions' }]}
      >
        {people.flatMap((person) => {
          const relationshipType = getRelationshipType(person.id);
          const relationship = findRelationship(person.id);

          if (relationshipType === '' || !relationship?.id) {
            return [];
          }

          return (
            <PersonRow
              key={person.id}
              person={person}
              relationshipType={relationshipType}
              relationshipId={relationship.id}
              fromClientProfileId={relationship.fromClientProfile.id}
              toClientProfileId={relationship.toClientProfile.id}
              onSelect={onSelectPerson}
              onUnlink={handleUnlinkRelationship}
              onChangeRelationshipType={handleChangeRelationshipType}
              isUnlinking={unlinkingPersonId === person.id}
              isUpdatingRelationship={isUpdatingRelationship}
            />
          );
        })}
      </DisplayTable>
    </Stack>
  );
}
